import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Page } from "@/components/layout/page";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";
import {
  EntityNotFound,
  EntityErrorState,
} from "@/components/layout/entity-state";
import { isValidUuid } from "@/lib/utils";
import { ActionBar } from "@/components/layout/action-bar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TonalInvitation } from "@/components/ui/tonal";
import { PaperCard } from "@/components/ui/paper-card";
import { Kicker } from "@/components/ui/kicker";
import { Dateline } from "@/components/ui/dateline";
import { PersonaAvatar } from "@/components/persona-identity";
import { criticalityLabel } from "@/components/experience/vocabulary";
import { CastPersonaDialog } from "@/components/experience/cast-persona-dialog";
import {
  SituationNarrative,
  SituationTension,
} from "@/components/experience/situation-narrative";
import {
  EditScenarioDialog,
  ScenarioDangerZone,
} from "@/components/experience/scenario-detail/edit-scenario-dialog";

type BoundScenarioItem = {
  scenarioId: string;
  personaId: string;
  personaName: string;
};

export function ScenarioEditPage() {
  const { id: projectId, outlineId } = useParams<{
    id: string;
    outlineId: string;
  }>();
  const isValidId = isValidUuid(outlineId);
  const utils = trpc.useUtils();
  const [editing, setEditing] = useState(false);
  const [showCast, setShowCast] = useState(false);

  const {
    data: outline,
    isLoading,
    error,
    refetch,
  } = trpc.outline.getById.useQuery(
    { id: outlineId ?? "" },
    { enabled: isValidId, retry: false },
  );
  useSetBreadcrumb(outline?.name);

  const { data: scenarioFallback } = trpc.scenario.getById.useQuery(
    { id: outlineId ?? "" },
    { enabled: isValidId && !!error, retry: false },
  );
  const { data: personasList } = trpc.persona.list.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId },
  );
  const { data: capabilitiesList } = trpc.capability.list.useQuery(
    { projectId: projectId ?? "" },
    { enabled: !!projectId },
  );

  const updateOutline = trpc.outline.update.useMutation({
    onSuccess: () => {
      if (outlineId) void utils.outline.getById.invalidate({ id: outlineId });
      if (projectId) void utils.outline.list.invalidate({ projectId });
      setEditing(false);
      toast.success("Scenario updated");
    },
    onError: (err) => toastTrpcError(err, "Failed to update scenario"),
  });

  if (isLoading) {
    return (
      <Page
        title={<Skeleton className="h-8 w-64" />}
        back={{ to: `/projects/${projectId}/scenarios`, label: "Scenarios" }}
      >
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }

  if (error || !outline) {
    const fb = scenarioFallback as
      | { outlineId?: string; personaId?: string | null }
      | undefined;
    if (fb?.outlineId && fb?.personaId && projectId) {
      return (
        <Navigate
          to={`/projects/${projectId}/scenarios/${fb.outlineId}#persona-${fb.personaId}`}
          replace
        />
      );
    }
    const backLink = {
      to: `/projects/${projectId ?? ""}/scenarios`,
      label: "Back to Scenarios",
    };
    if (!isValidId || error?.data?.code === "NOT_FOUND" || !outline) {
      return (
        <EntityNotFound
          title="Scenario not found"
          entityName="scenario"
          backLink={backLink}
        />
      );
    }
    return (
      <EntityErrorState
        title="Failed to load scenario"
        onRetry={() => refetch()}
        backLink={backLink}
      />
    );
  }

  const boundScenarios =
    (outline as { scenarios?: BoundScenarioItem[] }).scenarios ?? [];
  const boundPersonaIds = new Set(boundScenarios.map((s) => s.personaId));
  const castablePersonas = (personasList ?? []).filter(
    (p) => !boundPersonaIds.has(p.id),
  );
  const capabilities = capabilitiesList ?? [];
  const primaryCap = capabilities.find(
    (c) => c.id === outline.primaryCapabilityId,
  );
  const noCastable = castablePersonas.length === 0;

  return (
    <Page
      title={outline.name ?? "Scenario"}
      eyebrow="Edit scenario"
      back={{
        to: `/projects/${projectId}/scenarios/${outlineId}`,
        label: outline.name ?? "Scenario",
      }}
      maxWidth="max-w-[960px]"
      actions={
        <ActionBar
          primary={
            <Button onClick={() => setShowCast(true)} disabled={noCastable}>
              <Plus className="mr-2 h-4 w-4" />
              Add a persona
            </Button>
          }
          secondary={[
            <Button
              key="edit"
              variant="outline"
              onClick={() => setEditing(true)}
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit scenario
            </Button>,
          ]}
        />
      }
    >
      <div className="space-y-10">
        <Dateline
          items={[
            ...(primaryCap
              ? [{ label: "Capability", value: primaryCap.name }]
              : []),
            ...(outline.taskCriticality
              ? [
                  {
                    label: "Criticality",
                    value: criticalityLabel(outline.taskCriticality),
                  },
                ]
              : []),
            { label: "Personas", value: `${boundScenarios.length} cast` },
          ]}
        />

        <PaperCard padding="lg" className="space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <Kicker>Situation</Kicker>
            <button
              type="button"
              onClick={() => setEditing(true)}
              aria-label="Edit scenario"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              Edit
            </button>
          </div>
          {outline.narrative && (
            <SituationNarrative>{outline.narrative}</SituationNarrative>
          )}
          {outline.tension && (
            <SituationTension>{outline.tension}</SituationTension>
          )}
        </PaperCard>

        {Array.isArray(outline.humanTests) && outline.humanTests.length > 0 && (
          <section className="space-y-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <Kicker as="h2">Human tests</Kicker>
              <span className="text-sm text-muted-foreground">
                What each cast persona answers after their run
              </span>
            </div>
            <ol className="space-y-2.5">
              {outline.humanTests.map(
                (q: { id: string; question: string }, i: number) => (
                  <li key={q.id} className="flex items-baseline gap-3">
                    <span className="w-6 shrink-0 text-sm tabular-nums text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-base">{q.question}</span>
                  </li>
                ),
              )}
            </ol>
          </section>
        )}

        <section className="space-y-5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Kicker as="h2">Personas</Kicker>
            <span className="text-sm text-muted-foreground">
              One situation, rendered through each persona's lens
            </span>
          </div>
          {boundScenarios.length === 0 ? (
            <TonalInvitation
              size="lg"
              onClick={() => setShowCast(true)}
              disabled={noCastable}
              title={noCastable ? "No personas to add" : "Add a persona"}
              subtitle={
                noCastable
                  ? "Create a persona in this project first."
                  : "Adding a persona gives this scenario a runnable lens."
              }
            />
          ) : (
            <ul className="divide-y divide-border/60 rounded-lg border border-border/60 bg-background">
              {boundScenarios.map((s) => (
                <li key={s.scenarioId} className="px-5 py-4">
                  <div className="flex items-center gap-4">
                    <PersonaAvatar name={s.personaName} size="md" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/projects/${projectId}/scenarios/${outlineId}#persona-${s.personaId}`}
                        className="text-base font-semibold underline-offset-4 hover:underline"
                      >
                        {s.personaName}
                      </Link>
                      <div className="mt-0.5 text-sm text-muted-foreground">
                        As experienced by {s.personaName.split(" ")[0]}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <CastPersonaDialog
        open={showCast}
        onOpenChange={setShowCast}
        projectId={projectId ?? ""}
        outlineId={outlineId ?? ""}
        excludePersonaIds={Array.from(boundPersonaIds)}
      />
      <EditScenarioDialog
        open={editing}
        onOpenChange={setEditing}
        name={outline.name ?? ""}
        primaryCapabilityId={outline.primaryCapabilityId ?? null}
        taskCriticality={outline.taskCriticality}
        narrative={outline.narrative ?? ""}
        tension={outline.tension ?? ""}
        humanTests={outline.humanTests ?? []}
        capabilities={capabilities}
        isSaving={updateOutline?.isPending ?? false}
        onSave={(input) =>
          outlineId && updateOutline.mutate({ id: outlineId, ...input })
        }
      />
      <ScenarioDangerZone
        outlineId={outlineId ?? ""}
        projectId={projectId ?? ""}
      />
    </Page>
  );
}
